import { IconButton, Link, SvgIcon, Tooltip } from '@mui/material';
import {
  FileDocumentEditOutline as DescriptionIcon,
  Help as HelpIcon,
  Bell as NotificationsIcon,
  Magnify as SearchIcon,
  Cog as SettingsIcon
} from '@opengov/react-capital-assets';
import React from 'react';

import { NAV_COLOR_PALETTE, tooltipOffsetProps } from '../../Components/styleConstants';
import { DATA_OG, DATA_TEST, getDynamicDataOGId, getDynamicDataTestId } from '../../enums';
import { BadgeVariant, UtilityNames } from '../../types';

import { getBadgeAccessibilitySuffix } from './BadgeNotification';
import { ConditionalBadgeNotificationRenderer } from './ConditionalBadgeNotificationRenderer';

type UtilityIcons = {
  [UtilityNames.help]: typeof SvgIcon;
  [UtilityNames.notifications]: typeof SvgIcon;
  [UtilityNames.profile]: typeof SvgIcon;
  [UtilityNames.reports]: typeof SvgIcon;
  [UtilityNames.settings]: typeof SvgIcon;
  [UtilityNames.search]: typeof SvgIcon;
};

export const TRAY_BUTTON_ICONS: Partial<UtilityIcons> = {
  Help: HelpIcon,
  Notifications: NotificationsIcon,
  Reports: DescriptionIcon,
  Settings: SettingsIcon,
  Search: SearchIcon
};

export interface ITrayIconLinkProps {
  utilityName: UtilityNames | string;
  customMuiIcon?: typeof SvgIcon;
  stringIcon?: string;
  url: string;
  openInNewTab?: boolean;
  badgeContent?: number;
  linkComponent?: React.ElementType;
  badgeVariant?: BadgeVariant;
  onClick?(e: React.MouseEvent<HTMLElement>): void;
}

export const TrayIconLinkBase: React.ForwardRefRenderFunction<HTMLButtonElement, ITrayIconLinkProps> = (
  {
    utilityName,
    customMuiIcon,
    stringIcon,
    url,
    openInNewTab = false,
    linkComponent,
    badgeContent,
    onClick,
    badgeVariant
  },
  ref
) => {
  const baseLabel = `${utilityName} link`;
  const badgeSuffix = getBadgeAccessibilitySuffix(badgeContent, badgeVariant);
  const ariaLabel = `${baseLabel}${badgeSuffix}`;

  return (
    <Tooltip title={utilityName} placement="top" enterNextDelay={500} disableInteractive={true} {...tooltipOffsetProps}>
      <Link
        href={url}
        sx={{ color: 'inherit', borderRadius: '50% !important', height: 32, width: 32 }}
        underline="none"
        component={linkComponent || 'a'}
        target={openInNewTab ? '_blank' : undefined}
        aria-label={ariaLabel}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          if (onClick) onClick(e);
        }}
        data-test={getDynamicDataTestId(DATA_TEST.tray_icon_link, utilityName.toLowerCase())}
        data-og={getDynamicDataOGId(DATA_OG.tray_icon_link, utilityName)}
      >
        <IconButton
          ref={ref}
          sx={{
            color: NAV_COLOR_PALETTE.secondaryGray,
            width: 32,
            height: 32
          }}
          size="small"
          tabIndex={-1}
          aria-hidden="true"
        >
          <ConditionalBadgeNotificationRenderer
            utilityName={utilityName}
            customMuiIcon={customMuiIcon}
            stringIcon={stringIcon}
            badgeVariant={badgeVariant}
            badgeContent={badgeContent}
          />
        </IconButton>
      </Link>
    </Tooltip>
  );
};

export const TrayIconLink = React.forwardRef(TrayIconLinkBase);
